import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, DownloadSimple } from "@phosphor-icons/react";
import QrCardBlock from "@/components/QrCardBlock";

/**
 * /qr-card — printable QR that lands on /subscribe?src=qr.
 * Toolbar toggles:
 *   Layout: 1-up (single 4x6) or 4-up (letter, cut lines)
 *   Ink:    color (red accents) or mono (black & white printer safe)
 */

const headlineFor = () => (accent) => (
  <>
    The patient is{" "}
    <span className="italic font-light text-black/60">stable</span>
    <span style={{ color: accent }}>-ish.</span>
  </>
);

export default function QrCard() {
  const [origin, setOrigin] = useState("https://stableish.me");
  const [layout, setLayout] = useState("single"); // "single" | "bundle"
  const [ink, setInk] = useState("color"); // "color" | "mono"

  useEffect(() => {
    document.title = "Printable QR · Stable-ish";
    if (typeof window !== "undefined" && window.location?.origin) {
      const host = window.location.host;
      if (host === "stableish.me" || host === "www.stableish.me") {
        setOrigin("https://stableish.me");
      } else {
        setOrigin(window.location.origin);
      }
    }
  }, []);

  const qrUrl = useMemo(() => `${origin}/subscribe?src=qr`, [origin]);
  const isMono = ink === "mono";

  const onDownloadSvg = () => {
    const svgEl = document.querySelector(".qr-card-printable svg");
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stableish-qr-subscribe-${ink}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleBase =
    "font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors";
  const on = "bg-[#FF2A2A] border-[#FF2A2A] text-white";
  const off = "border-white/20 text-white/70 hover:border-white/40 hover:text-white";

  const card = (
    <QrCardBlock
      qrUrl={qrUrl}
      variant={layout === "bundle" ? "bundle" : "single"}
      mono={isMono}
      headline={headlineFor()}
      subhead="50+ bedside tools. Drug doses, drips, calculators. Written by nurses, for nurses."
      scanLabel="scan to stay in the loop"
    />
  );

  return (
    <main
      className="min-h-screen bg-[#0a0a0a] text-white"
      data-testid="qr-card-page"
    >
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: #ffffff !important; }
          .no-print { display: none !important; }
          .print-area { padding: 0 !important; max-width: none !important; }
          .qr-card-printable { box-shadow: none !important; page-break-inside: avoid; }
          .qr-bundle-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 0 !important;
            justify-items: center;
            align-items: center;
            width: 100% !important;
            background: transparent !important;
            border: none !important;
          }
          .qr-bundle-cell { padding: 6px !important; }
        }
      `}</style>

      <div className="no-print border-b border-white/10 bg-black/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-[#FF2A2A] transition-colors"
            data-testid="qr-card-back-link"
          >
            <ArrowLeft size={14} weight="bold" />
            back to home
          </Link>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-stretch" role="tablist" aria-label="Layout">
              <button
                type="button"
                onClick={() => setLayout("single")}
                data-testid="qr-card-layout-single"
                aria-pressed={layout === "single"}
                className={`${toggleBase} ${layout === "single" ? on : off}`}
                style={{ borderRight: "none" }}
              >
                1-up · 4×6
              </button>
              <button
                type="button"
                onClick={() => setLayout("bundle")}
                data-testid="qr-card-layout-bundle"
                aria-pressed={layout === "bundle"}
                className={`${toggleBase} ${layout === "bundle" ? on : off}`}
              >
                4-up · letter
              </button>
            </div>

            <div className="inline-flex items-stretch" role="tablist" aria-label="Ink">
              <button
                type="button"
                onClick={() => setInk("color")}
                data-testid="qr-card-ink-color"
                aria-pressed={ink === "color"}
                className={`${toggleBase} ${ink === "color" ? on : off}`}
                style={{ borderRight: "none" }}
              >
                Color
              </button>
              <button
                type="button"
                onClick={() => setInk("mono")}
                data-testid="qr-card-ink-mono"
                aria-pressed={ink === "mono"}
                className={`${toggleBase} ${ink === "mono" ? on : off}`}
              >
                B &amp; W
              </button>
            </div>

            <button
              type="button"
              onClick={onDownloadSvg}
              data-testid="qr-card-download-svg"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF2A2A] hover:text-[#FF2A2A] text-white/80 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors"
            >
              <DownloadSimple size={14} weight="bold" />
              download svg
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="qr-card-print-button"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors"
            >
              <Printer size={14} weight="bold" />
              print
            </button>
          </div>
        </div>
      </div>

      <div className="no-print max-w-[1100px] mx-auto px-5 md:px-10 pt-12 pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-3">
          // break-room ready · conference-ready
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
          Printable{" "}
          <span className="italic font-light text-white/70">
            QR card{layout === "bundle" ? "s" : ""}.
          </span>
        </h1>
        <p className="mt-4 text-base text-white/60 max-w-xl leading-relaxed">
          {layout === "single" ? (
            <>
              Pin this on your unit&rsquo;s bulletin board, tape it to the
              break-room fridge, or hand it out at a conference. Prints one
              4×6&Prime; card per sheet.
            </>
          ) : (
            <>
              Prints{" "}
              <strong className="text-white">four cards per letter sheet</strong>{" "}
              — cut along the dashed lines and hand them out.
            </>
          )}{" "}
          Every scan is tagged{" "}
          <code className="font-mono text-[#FF2A2A] text-sm">src=qr</code> so
          you&rsquo;ll see it separately in the admin dashboard.
        </p>
        <p className="mt-3 font-mono text-xs text-white/40 break-all">
          Encoded URL: <span className="text-white/70">{qrUrl}</span>
        </p>
      </div>

      <div className="print-area max-w-[1100px] mx-auto px-5 md:px-10 pb-24 pt-4 flex justify-center">
        {layout === "single" ? (
          <div className="shadow-[0_20px_80px_-20px_rgba(255,42,42,0.35)]">
            {card}
          </div>
        ) : (
          <div
            className="qr-bundle-grid grid grid-cols-2 gap-3 shadow-[0_20px_80px_-20px_rgba(255,42,42,0.35)]"
            data-testid="qr-card-bundle-grid"
            style={{
              width: "8in",
              maxWidth: "100%",
              padding: "8px",
              background: "#0f0f0f",
              border: "1px dashed rgba(255,255,255,0.15)",
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="qr-bundle-cell flex items-center justify-center"
                style={{
                  padding: "8px",
                  borderRight: i % 2 === 0 ? "1px dashed rgba(255,255,255,0.25)" : "none",
                  borderBottom: i < 2 ? "1px dashed rgba(255,255,255,0.25)" : "none",
                }}
              >
                {card}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
